/* ============================================================================
   agmf-policy.js — whether to show a piece of content again.

   This is the DECIDING half. agmf-seen.js records and decides nothing; this
   file decides and records nothing. They are separate files on purpose: a
   layer that both records and hides cannot be trusted to have recorded
   honestly, and the record is the thing every later question depends on.

   WHY IT RUNS HERE AND NOT IN THE EXPORT
   The export bakes ONE feed document that every shopper reads byte-for-byte.
   "Has this person already seen it?" is a question only their browser can
   answer, so the filtering has to happen at render. What the export carries
   is the RULES (`feed.seenPolicy`), which costs nothing — the theme is
   fetching that document anyway, so there is no second round trip and no
   delay before the first card.

   THE RULES, chosen per content type in Agilite Media
     show   show it again — today's behaviour, and the default for every kind
     back   push it behind content they have not been shown
     rest   hold it back for N days, then let it return
     once   never again in this browser

   WHAT COUNTS AS SEEN is one choice for all kinds (`counts`): glanced (half
   the card on screen), looked (…and it stayed ~2s), or opened (they played or
   read it). Default `looked` — a card that flicked past is not something
   somebody has seen, and treating it as one hides content nobody ever got.

   THE FLOOR IS NOT OPTIONAL
   Every rule here can only ever REMOVE content, and the feed is endless. The
   most likely way this feature fails is by working too well: a returning
   shopper arrives to an empty scroll. So the floor is honoured last and wins
   over every rule — items come back, least-recently-seen first, until the
   feed has enough. A repeat is a worse experience; nothing is a bug.

   SAYS WHAT IT DID, IN PREVIEW ONLY
   Same gate every other engine toast uses: never on the live theme, and only
   when the panel has debug toasts on.
   ============================================================================ */
(function () {
  'use strict';

  if (window.AGMF_Policy) return;

  var RUNG = { glanced: 1, looked: 2, opened: 3 };
  var MODES = { show: 1, back: 1, rest: 1, once: 1 };

  function log() {
    try {
      if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
        console.log.apply(console, ['[agmf-policy]'].concat([].slice.call(arguments)));
      }
    } catch (e) {}
  }

  // The last plan, so the preview toast can explain a decision the shopper
  // cannot see the reason for.
  var last = null;

  /**
   * items  the feed's items, in export order
   * policy feed.seenPolicy — {counts, floor, types:{contentType:{mode,times,days}}}
   * returns the items to build the scroll from
   */
  function plan(items, policy) {
    var list = (items && items.length) ? items.slice() : [];
    last = { held: [], pushed: [], restored: [], fromCard: 0, total: list.length, on: false };
    if (!list.length) return list;

    // Nothing chosen: every kind still says "show it again", which is what the
    // feed has always done. Do not touch the order.
    var types = (policy && policy.types) || {};
    var fromCard = (policy && policy.fromCard) || 'show';
    if (!Object.keys(types).length && fromCard === 'show') return list;

    // No record means no answer. Never guess "they have seen it" — hiding
    // content on a hunch is the one mistake with no upside.
    var seen = window.AGMF_Seen;
    if (!seen || typeof seen.info !== 'function') {
      log('no record available — showing everything');
      return list;
    }
    last.on = true;

    var needRung = RUNG[(policy && policy.counts) || 'looked'] || RUNG.looked;
    var keep = [], back = [], held = [];

    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      var rule = item && item.contentType ? types[item.contentType] : null;
      var mode = rule && MODES[rule.mode] ? rule.mode : 'show';
      if (!item.ck) { keep.push(item); continue; }

      var info = seen.info(item.ck);

      // ALREADY SHOWN ON THE PRODUCT CARD. A surface rule, checked before the
      // per-kind one and independent of it: the card at the top of this page
      // drew on the same library, and a shopper who has just been shown a
      // picture there has no use for it again fifteen cards later.
      //
      // It reads what the LEDGER recorded, not what the card contains - the
      // card can hold more than anybody scrolled through. That means it acts
      // on what they saw LAST time as well as this: the feed's order is
      // decided once, when it loads, and the card is above it.
      if (fromCard !== 'show' && info && info.card && info.depth >= needRung) {
        if (fromCard === 'back') { back.push(item); last.pushed.push(item.ck); continue; }
        held.push({ item: item, info: info });
        last.held.push(item.ck);
        last.fromCard++;
        continue;
      }
      if (mode === 'show') { keep.push(item); continue; }
      // Not seen at the chosen depth, or not seen often enough yet.
      if (!info || info.depth < needRung || info.times < (rule.times || 1)) {
        keep.push(item); continue;
      }
      if (mode === 'back') { back.push(item); last.pushed.push(item.ck); continue; }
      if (mode === 'rest') {
        // The rest is over: it may come back, and it comes back in its place
        // rather than at the end — the point of resting it was the gap.
        if (info.days >= (rule.days || 14)) { keep.push(item); continue; }
      }
      held.push({ item: item, info: info });
      last.held.push(item.ck);
    }

    // Demoted content sits behind everything they have not been shown. The
    // scroll builder rotates content KINDS over this order, so "behind" means
    // later in its own kind, not banished — which is what pushing back should
    // mean on a feed that never ends.
    var out = keep.concat(back);

    // THE FLOOR, honoured last so it beats every rule above.
    var floor = policy && typeof policy.floor === 'number' ? policy.floor : 8;
    if (out.length < floor && held.length) {
      held.sort(function (a, b) {
        return (b.info.days - a.info.days) || (a.info.times - b.info.times);
      });
      while (out.length < floor && held.length) {
        var back_in = held.shift();
        out.push(back_in.item);
        last.restored.push(back_in.item.ck);
        var at = last.held.indexOf(back_in.item.ck);
        if (at >= 0) last.held.splice(at, 1);
      }
    }

    log(last.held.length, 'held back ·', last.pushed.length, 'pushed behind ·',
        last.restored.length, 'brought back to keep the feed full ·', out.length, 'showing');
    announce();
    return out;
  }

  // Preview only, never live: the same gate the cart suggestions use.
  function announce() {
    try {
      var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      var eng = window.SE_Engine;
      if (role === 'main' || !eng || !eng.debugToastsEnabled ||
          typeof eng._emitInfoToast !== 'function') return;
      if (!last || !last.on) return;
      if (!last.held.length && !last.pushed.length) return;
      var parts = [];
      if (last.held.length) {
        parts.push(last.held.length + ' held back as already seen' +
                   (last.fromCard ? ' (' + last.fromCard + ' of them on the product card above)' : ''));
      }
      if (last.pushed.length) parts.push(last.pushed.length + ' pushed behind newer content');
      if (last.restored.length) {
        parts.push(last.restored.length + ' brought back so the feed still has ' +
                   last.total + ' item(s) to show');
      }
      eng._emitInfoToast('agmfSeen', 'FEED · ALREADY SEEN',
        parts.join(', ') + ' (this browser only; the record is not shared between devices)',
        { source: 'reco', kind: 'info' });
    } catch (e) {}
  }

  window.AGMF_Policy = {
    plan: plan,
    // What the last plan did, for the preview panel and for a console check.
    explain: function () { return last ? JSON.parse(JSON.stringify(last)) : null; }
  };
})();
